// Status: active. User-selected marketing audience presets; no onboarding writes.
import { z } from 'zod';
export const audiencePresetKeySchema = z.enum(['all-donors', 'recurring-donors', 'lapsed-donors', 'high-value-donors', 'frequent-givers', 'one-time-multi-gift', 'failed-payment']);
const audienceSelectionObject = z.object({
  presetKey: audiencePresetKeySchema, catalogVersion: z.literal('1'),
  targetToken: z.string().regex(/^[a-f0-9]{64}$/),
  threshold: z.number().finite().min(0).max(100000000).optional(),
}).strict();
function validateSelection(selection: z.infer<typeof audienceSelectionObject>, ctx: z.RefinementCtx) {
  const { presetKey, threshold } = selection;
  if (presetKey === 'failed-payment') ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['presetKey'], message: 'This suggested segment is unavailable' });
  if (threshold === undefined) return;
  if (!['high-value-donors', 'frequent-givers', 'one-time-multi-gift'].includes(presetKey)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['threshold'], message: 'This segment has no adjustable threshold' });
  } else if (presetKey !== 'high-value-donors' && !Number.isInteger(threshold)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['threshold'], message: 'Payment count must be a whole number' });
  }
}
export const audienceSelectionSchema = audienceSelectionObject.superRefine(validateSelection);
const createAudienceObject = audienceSelectionObject.extend({
  intentProtocol: z.literal('durable-v1'), name: z.string().trim().min(1).max(120), requestId: z.string().uuid(),
}).strict();
export const createAudienceInputSchema = createAudienceObject.superRefine(validateSelection);
export const createAudienceOutputSchema = z.object({ id: z.string().min(1), pocketflowsId: z.string().min(1), name: z.string(), filter: z.string() });
export const audiencePresetSchema = z.object({
  key: audiencePresetKeySchema, name: z.string(), description: z.string(), filter: z.string(),
  threshold: z.number().nullable(), thresholdLabel: z.string().nullable(),
});
export const audienceCatalogOutputSchema = z.object({ catalogVersion: z.literal('1'), targetToken: z.string(), presets: z.array(audiencePresetSchema) });
export const audiencePreviewOutputSchema = z.object({
  estimatedMatches: z.number().int().nonnegative().nullable(), evaluatedAt: z.string(), oldestSyncedAt: z.string().nullable(), complete: z.boolean(),
  evaluatedContacts: z.number().int().nonnegative(), unsyncedContacts: z.number().int().nonnegative(), filter: z.string(),
});
export const audienceDraftInputSchema = z.discriminatedUnion('source', [
  createAudienceObject.extend({ source: z.literal('preset'), campaignName: z.string().trim().min(1).max(120) }).strict(),
  z.object({ intentProtocol: z.literal('durable-v1'), source: z.literal('existing'), segmentId: z.string().min(1), targetToken: z.string().regex(/^[a-f0-9]{64}$/), requestId: z.string().uuid(), campaignName: z.string().trim().min(1).max(120) }).strict(),
]).superRefine((input, ctx) => {
  if (input.source === 'preset') validateSelection(input, ctx);
});
// segmentId is the CharityStack mirror ID, matching broadcast references.
export const audienceDraftOutputSchema = z.object({ id: z.string().min(1), pocketflowsId: z.string().min(1), name: z.string(), broadcastType: z.literal('CAMPAIGN'), status: z.literal('draft'), segmentId: z.string() });
export type AudienceSelection = z.infer<typeof audienceSelectionSchema>;
export type CreateAudienceInput = z.infer<typeof createAudienceInputSchema>;
export type AudienceCatalog = z.infer<typeof audienceCatalogOutputSchema>;
export type AudiencePreview = z.infer<typeof audiencePreviewOutputSchema>;
export type CreatedAudience = z.infer<typeof createAudienceOutputSchema>;
export type AudienceDraftInput = z.infer<typeof audienceDraftInputSchema>;
export type AudienceDraft = z.infer<typeof audienceDraftOutputSchema>;
